import React, { useContext } from 'react'
import { ClimaOptionsContext } from '..'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { OptionContent } from '../style'
import { FormFields } from '~/components/FormFields'
import { useTranslation } from 'react-i18next'
import { Form, Table } from 'semantic-ui-react'
import { formatNumber } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'
import Nitrogen from '../GrassSilage/Results/Nitrogen'
import Phosphor from '../GrassSilage/Results/Phosphor'
import Kalium from '../GrassSilage/Results/Kalium'
import GroupOne from '../GrassSilage/Results/GroupOne'
import GroupTwo from '../GrassSilage/Results/GroupTwo'
import GroupThree from '../GrassSilage/Results/GroupThree'

export const mineralDataFields = [
  {
    name: 'Stickstoff (kg/dt)',
    key: 'n',
    type: 'input'
  },
  {
    name: 'Phosphat (P2O5) (kg/dt)',
    key: 'p2o5',
    type: 'input'
  },
  {
    name: '<span>Kalium (K<sub>2</sub>O) (kg/dt)</span>',
    key: 'k2o',
    type: 'input'
  }
]

const ThirdStep:React.FC = () => {
  const { t } = useTranslation()
  const { mineralFertilizers, mineralFertilizerApplicationTechniques, limeTypes } = useContext(ClimaOptionsContext)
  const { control, getValues, setValue, clearErrors } = useFormContext()

  const { fields, replace } = useFieldArray({
    control,
    name: 'maizeSilage.mineralFertilizers',
    keyName: 'key'
  })

  const watchLimeType = useWatch({ control, name: 'maizeSilage.limeType' })

  const tableColumnHeaders = [
    {
      name: 'Düngeart',
      key: 'label',
      type: 'fertilizer-fields'
    },
    {
      name: 'Ausbringmenge',
      key: 'value',
      type: 'input'
    },
    {
      name: 'Ausbringungstechnik',
      key: 'mineralFertilizerApplicationTechniqueId',
      type: 'select',
      optionsKey: 'mineralFertilizerApplicationTechniques'
    }
  ];

  function renderInputCellValue(name) {
    return <FormFields.Input
      name={name}
      placeholder={' dt/ha'}
      suffix=' dt/ha'
      control={control}
      onChange={(event) => {
        setValue(name, event?.target?.value?.replace('dt/ha', ''))
        clearErrors(name)
      }}
      typeInput={'controled'}
      withoutAsterisk
      noErrorMessage
      allowDecimals
      decimalScale={2}
    />
  }

  function renderSelect(name) {
    return <FormFields.Select
      name={name}
      style={{ width: 380 }}
      onChange={(e, data) => {
        setValue(name, data.value)
        clearErrors(name)
      }}
      placeholder={'Art der Ausbringungstechhnik'}
      control={control}
      scrolling
      withoutAsterisk
      noErrorMessage
      options={mineralFertilizerApplicationTechniques.map(mineralFert => ({ value: mineralFert.id, text: mineralFert.label }))}
    />
  }

  function renderFertilizerFieldCell(mineralFertilizer) {
    return <>
      <div>{mineralFertilizer.label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <small style={{ fontSize: '85%' }}>
          {t('[Stickstoff: {{n}} kg/dt, Phosphat (P2O5): {{p2o5}} kg/dt]...',
            {
              n: formatNumber(mineralFertilizer?.n?.replace(',', '.'), 2),
              p2o5: formatNumber(mineralFertilizer?.p2o5?.replace(',', '.'), 2)
            }
          )}
        </small>
        <div style={{ display: 'flex', gap: 5, marginLeft: 7 }}>
          <InfoPopup
            position={'top center'}
            styleIcon={{ fontSize: '1.2em' }}
            content={<small style={{ fontSize: '85%' }}>
              {mineralDataFields.map(mineralField => {
                return <div
                  key={mineralField.key}
                  dangerouslySetInnerHTML={{ __html: `${mineralField.name}: ${formatNumber(mineralFertilizer[mineralField.key]?.replace(',', '.'), 2)} kg/dt` }}
                />
              })}
            </small>}
          />
        </div>
      </div>
    </>
  }

  function renderCell(mineralFertilizer, name, column) {
    switch(column.type){
      case 'input':
        return renderInputCellValue(name)
      case 'select':
        return renderSelect(name)
      case 'fertilizer-fields':
        return renderFertilizerFieldCell(mineralFertilizer)
      default:
        return mineralFertilizer[column.key]
    }
  }

  function renderTableBody() {
    return fields.map((mineralFertilizer, indexFertilizer) => {
      return <Table.Row key={mineralFertilizer.key}>
        {tableColumnHeaders.map(column => {
          const name = `maizeSilage.mineralFertilizers[${indexFertilizer}].${column.key}`
          return <Table.Cell key={`${mineralFertilizer.key}-${column.key}`}>
            {renderCell(mineralFertilizer, name, column)}
          </Table.Cell>
        })}
      </Table.Row>
    })
  }

  function renderTable() {
    return <Table>
      <Table.Header>
        <Table.Row>
          {tableColumnHeaders.map(header => {
            return <Table.HeaderCell key={header.key}>
              {header.name}
            </Table.HeaderCell>
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  function renderMineralResults() {
    return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'baseline' }}>
      <Nitrogen keySilage={'maizeSilage'}/>
      <Phosphor keySilage={'maizeSilage'}/>
      <Kalium keySilage={'maizeSilage'}/>
      <GroupOne keySilage={'maizeSilage'}/>
      <GroupTwo keySilage={'maizeSilage'}/>
      <GroupThree keySilage={'maizeSilage'}/>
    </div>
  }

  function renderLimeFields() {
    const displayQuicklimeAmount = watchLimeType === 1 || watchLimeType === 3
    const displayLimestoneAmount = watchLimeType === 2 || watchLimeType === 3
    return <>
      <FormFields.Select
        name={'maizeSilage.limeType'}
        label={'Welcher Kalk wurde ausgebracht?'}
        placeholder={'Welcher Kalk wurde ausgebracht?'}
        control={control}
        scrolling
        withoutAsterisk
        noErrorMessage
        options={limeTypes.map(limeType => ({ value: limeType.key, text: limeType.label }))}
      />
      <Form.Group widths={'equal'}>
        {displayQuicklimeAmount && <FormFields.Input
          control={control}
          name={'maizeSilage.quicklimeAmount'}
          label={'Branntkalk'}
          onChange={(event) => {
            setValue('maizeSilage.quicklimeAmount', event?.target?.value.replace('kg /ha', ''))
            clearErrors('maizeSilage.quicklimeAmount')
          }}
          noErrorMessage
          typeInput='controled'
          suffix=' kg /ha'
          placeholder={'in kg /ha'}
        />}
        {displayLimestoneAmount && <FormFields.Input
          control={control}
          name={'maizeSilage.limestoneAmount'}
          label={'Kalkstein'}
          noErrorMessage
          onChange={(event) => {
            setValue('maizeSilage.limestoneAmount', event?.target?.value.replace('kg /ha', ''))
            clearErrors('maizeSilage.limestoneAmount')
          }}
          typeInput='controled'
          suffix=' kg /ha'
          placeholder={'in kg /ha'}
        />}
      </Form.Group>
    </>
  }

  return <>
    <OptionContent>
      <FormFields.Select
        label={'Welche Mineraldünger haben Sie aufgebracht?'}
        control={control}
        placeholder='Art des Mineraldünger'
        id={'maizeSilage.mineralFertilizers'}
        name=''
        value={fields.map(fertilizer => fertilizer['id'])}
        options={mineralFertilizers.map(type => ({ value: type.id, text: type.label }))}
        onChange={(_, { value }: {value: number[]}) => {
          const mineralFertilizerValues = value.map(id => mineralFertilizers.find(fertilizer => fertilizer.id === id))
          const minerals = getValues('maizeSilage.mineralFertilizers')
          const includedmineralFertilizerValues = value.map(id => minerals.find(field => field['id'] === id) || mineralFertilizerValues.find(n => n.id === id))

          if(includedmineralFertilizerValues.length > 0) {
            const targetElement = document.getElementById('maizeSilage.mineralFertilizers')
            targetElement?.classList?.remove('error');
          }

          replace(includedmineralFertilizerValues)
        }}
        multiple
        search
        noResultsMessage={t('No results found....')}
        noErrorMessage
        withoutAsterisk
      />
      {fields.length > 0 && renderTable()}
      {renderLimeFields()}
      {fields.length > 0 && renderMineralResults()}
    </OptionContent>
  </>
}
export default ThirdStep